import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase.js';

const getAdminDashboardFn = httpsCallable(functions, 'getAdminDashboard');
const getUserContentFn = httpsCallable(functions, 'getUserContent');
const updateGenerationSettingsFn = httpsCallable(functions, 'updateGenerationSettings');
const testGenerationSettingsFn = httpsCallable(functions, 'testGenerationSettings');
const updateCommerceSettingsFn = httpsCallable(functions, 'updateCommerceSettings');
const updateAccessSettingsFn = httpsCallable(functions, 'updateAccessSettings');
const saveMarketplaceLabelFn = httpsCallable(functions, 'saveMarketplaceLabel');
const deleteMarketplaceLabelFn = httpsCallable(functions, 'deleteMarketplaceLabel');
const updateLandingShowcaseFn = httpsCallable(functions, 'updateLandingShowcase');
const previewInvoiceFn = httpsCallable(functions, 'previewInvoice');
const upsertSchoolFn = httpsCallable(functions, 'upsertSchool');
const listSchoolsFn = httpsCallable(functions, 'listSchools');
const upsertRecommenderFn = httpsCallable(functions, 'upsertRecommender');
const listRecommendersFn = httpsCallable(functions, 'listRecommenders');
const adminCreditWalletFn = httpsCallable(functions, 'adminCreditWallet');
const setUnlimitedAccessFn = httpsCallable(functions, 'setUnlimitedAccess');
const adminCreditSchoolWalletFn = httpsCallable(functions, 'adminCreditSchoolWallet');
const setSchoolMarketplaceSellingFn = httpsCallable(functions, 'setSchoolMarketplaceSelling');
const setSchoolPinnedLabelsFn = httpsCallable(functions, 'setSchoolPinnedLabels');
const resolveMarketplaceComplaintFn = httpsCallable(functions, 'resolveMarketplaceComplaint');
const resolveSellerRecoveryFn = httpsCallable(functions, 'resolveSellerRecovery');
const getKitTestingSettingsFn = httpsCallable(functions, 'getKitTestingSettings');
const updateKitTestingSettingsFn = httpsCallable(functions, 'updateKitTestingSettings');
const getKitSourceFn = httpsCallable(functions, 'getKitSource');
const getGenerationRunsFn = httpsCallable(functions, 'getGenerationRuns');
const listAllActiveAgentSessionsFn = httpsCallable(functions, 'listAllActiveAgentSessions');
const getGenerationPipelineStatsFn = httpsCallable(functions, 'getGenerationPipelineStats');
const getKitDiagnosticsFn = httpsCallable(functions, 'getKitDiagnostics');
const runKitGapAnalysisFn = httpsCallable(functions, 'runKitGapAnalysis');
const getLastKitGapAnalysisFn = httpsCallable(functions, 'getLastKitGapAnalysis');
// timeout set explicitly above the callables' own server-side budget (480s/
// 540s — see kitGapReplay.js/index.js) — the SDK default of 70s would abort
// client-side well before either could finish a real generation call.
const replayKitGapExampleFn = httpsCallable(functions, 'replayKitGapExample', { timeout: 560000 });
const flagKitGapTopicsFn = httpsCallable(functions, 'flagKitGapTopics');
const runEmbeddingBackfillFn = httpsCallable(functions, 'runEmbeddingBackfill');
const getEmbeddingBackfillStatusFn = httpsCallable(functions, 'getEmbeddingBackfillStatus');
const runTopicOwnerBackfillFn = httpsCallable(functions, 'runTopicOwnerBackfill');
const getTopicOwnerBackfillStatusFn = httpsCallable(functions, 'getTopicOwnerBackfillStatus');

export async function getAdminDashboard() {
  const res = await getAdminDashboardFn();
  return res.data;
}

// Drill-down for one user: every course/topic they've created, with
// per-topic API request counts (see functions/admin.js getUserContent).
export async function getUserContent(uid) {
  const res = await getUserContentFn({ uid });
  return res.data; // { uid, courses, totals }
}

export async function updateGenerationSettings(settings) {
  const res = await updateGenerationSettingsFn({ ...settings, idempotencyKey: crypto.randomUUID() });
  return res.data.settings;
}

export async function testGenerationSettings(settings) {
  const res = await testGenerationSettingsFn({ ...settings, idempotencyKey: crypto.randomUUID() });
  return res.data;
}

// ── Commerce (admin-only) — every percentage that moves money lives in
// these settings; nothing is hardcoded in the purchase flow. ─────────────
export async function updateCommerceSettings(settings) {
  const res = await updateCommerceSettingsFn({ ...settings, idempotencyKey: crypto.randomUUID() });
  return res.data.settings;
}

// Pilot-phase switches — see functions/accessSettings.js. Not money-moving,
// so no idempotency key (mirrors updateKitTestingSettings below).
export async function updateAccessSettings(settings) {
  const res = await updateAccessSettingsFn(settings);
  return res.data.settings;
}

// Curated marketplace labels — see functions/marketplaceLabels.js.
export async function saveMarketplaceLabel(payload) {
  const res = await saveMarketplaceLabelFn(payload);
  return res.data;
}

export async function deleteMarketplaceLabel(labelId) {
  await deleteMarketplaceLabelFn({ labelId });
}

// Landing showcase — which active marketplace listings' created content is
// featured in the public landing-page sample gallery (ordered listing IDs).
export async function updateLandingShowcase(listingIds) {
  const res = await updateLandingShowcaseFn({ listingIds });
  return res.data; // { ok, listingIds }
}

// Sample invoice — runs the REAL computeSplit server-side (same code path as
// an actual purchase) so the admin previews exact rupee-for-rupee math, plus
// a seat-cost preview (the platform's per-seat/term school revenue).
export async function previewInvoice({ listRupees, seatQty = 1 }) {
  const res = await previewInvoiceFn({ listRupees, seatQty });
  return res.data; // { split, seatPreview, settingsUsed }
}

export async function upsertSchool(school) {
  const res = await upsertSchoolFn({ ...school, idempotencyKey: crypto.randomUUID() });
  return res.data; // { code }
}

export async function listSchools() {
  const res = await listSchoolsFn();
  return res.data; // { settings, schools }
}

// Recommenders — a person who onboards a school and earns a % of that
// school's own share for a fixed number of years, funded from the
// platform's commission (see functions/recommenders.js + commerce.js).
// The code they're given is attached to a school ONLY from the school form
// below — never surfaced to students or at checkout.
export async function upsertRecommender(recommender) {
  const res = await upsertRecommenderFn({ ...recommender, idempotencyKey: crypto.randomUUID() });
  return res.data; // { code }
}

export async function listRecommenders() {
  const res = await listRecommendersFn();
  return res.data; // { recommenders }
}

export async function adminCreditWallet({ email, amountRupees, direction = 'credit', note = '' }) {
  const res = await adminCreditWalletFn({ email, amountRupees, direction, note, idempotencyKey: crypto.randomUUID() });
  return res.data; // { ok, uid, balancePaise }
}

// Recent Users tick-mark — grants/revokes unlimited access (no wallet
// metering at all: generation, Ask AI, viewing) for one user.
export async function setUnlimitedAccess({ uid, unlimited }) {
  const res = await setUnlimitedAccessFn({ uid, unlimited });
  return res.data; // { ok, uid, unlimitedAccess }
}

// Fund (or debit) a school's prepaid wallet — the pool that pays for seat
// purchases and, when enabled, student/teacher generation.
export async function adminCreditSchoolWallet({ code, amountRupees, direction = 'credit', note = '' }) {
  const res = await adminCreditSchoolWalletFn({ code, amountRupees, direction, note, idempotencyKey: crypto.randomUUID() });
  return res.data; // { ok, code, balancePaise }
}

// School Partners table toggle — enables/disables a school's students from
// selling their creations in the marketplace (admin-only kill switch).
export async function setSchoolMarketplaceSelling({ code, enabled }) {
  const res = await setSchoolMarketplaceSellingFn({ code, enabled });
  return res.data; // { ok, code, marketplaceSellingEnabled }
}

// Associates curated labels with a school — see functions/schools.js
// setSchoolPinnedLabels for what this actually does (provisions free
// access + seeds pins for current students; new students get seeded
// lazily at their first sign-in).
export async function setSchoolPinnedLabels({ code, labelIds }) {
  const res = await setSchoolPinnedLabelsFn({ code, labelIds });
  return res.data; // { ok, code, labelIds, listingCount, grantedCount, studentsSeeded }
}

export async function resolveMarketplaceComplaint(payload) {
  const res = await resolveMarketplaceComplaintFn({ ...payload, idempotencyKey: crypto.randomUUID() });
  return res.data; // { ok }
}

export async function resolveSellerRecovery(payload) {
  const res = await resolveSellerRecoveryFn({ ...payload, idempotencyKey: crypto.randomUUID() });
  return res.data; // { ok }
}

// ── Kit Test Lab (admin-only, part of the Dynamic Experiment Test Area) —
// which in-house rendering kits (functions/libs/*.js) an admin has selected
// to smoke-test, and fetching their raw source to compose a sandboxed-iframe
// demo. Never used by real course generation. ─────────────────────────────
export async function getKitTestingSettings() {
  const res = await getKitTestingSettingsFn();
  return res.data; // { enabledKits }
}

export async function updateKitTestingSettings(enabledKits) {
  const res = await updateKitTestingSettingsFn({ enabledKits });
  return res.data; // { ok, enabledKits }
}

export async function getKitSource(kits) {
  const res = await getKitSourceFn({ kits });
  return res.data; // { sources: { [key]: { globalName, source } } }
}

// ── Agent Activity (admin-only) — makes the generator/sandbox-validator/
// fact-checker/fidelity-checker pipeline visible, and the "learner agent"
// that pools recurring struggle themes into kit-gap suggestions. See
// functions/generationRunLog.js and functions/kitGapMiner.js. ─────────────
export async function getGenerationRuns({ limit = 40, onlyWithFindings = false } = {}) {
  const res = await getGenerationRunsFn({ limit, onlyWithFindings });
  return res.data; // { runs: [{ sessionId, uid, topicType, instructionPreview, status, attempts: [...], findingCount }] }
}

// Every currently in-flight generation across every account (admin-only —
// see functions/agentSessionControl.js's listAllActiveAgentSessions),
// meant to be polled while an admin has the panel open rather than fetched
// once, since "currently" is the whole point.
export async function listAllActiveAgentSessions() {
  const res = await listAllActiveAgentSessionsFn();
  return res.data.sessions; // [{ sessionId, uid, email, pipeline, courseId, courseTitle, topicId, topicTitle, instructionPreview, status, segmentIndex, roundIndex, apiCallCount, tokensSpent, cancelRequested, startedAt, updatedAt, heartbeatAt }]
}

// Aggregated view over a recent window of runs (see functions/admin.js
// reducePipelineStats for the exact shape) — success rate, repairs/run,
// tool-calling hit rate, tokens (real + estimated share), prompt-cache hit
// rate, per-stage timing. Powers the "Pipeline Performance" section above
// the raw session list.
export async function getGenerationPipelineStats({ limit = 500 } = {}) {
  const res = await getGenerationPipelineStatsFn({ limit });
  return res.data;
}

export async function getKitDiagnostics({ limit = 500 } = {}) {
  const res = await getKitDiagnosticsFn({ limit });
  return res.data; // { kits: [{key, readCount, issueCount, issues:[{problem, correction, at}]}], totalReads, totalIssues }
}

export async function runKitGapAnalysis() {
  const res = await runKitGapAnalysisFn();
  return res.data; // { themes: [...], sampledCount, provider, model, analyzedAt } | { themes: [], note }
}

export async function getLastKitGapAnalysis() {
  const res = await getLastKitGapAnalysisFn();
  return res.data; // cached result of the last runKitGapAnalysis call, or { themes: [], sampledCount: 0, analyzedAt: null }
}

// Verification step for one theme example (see functions/kitGapReplay.js) —
// a real, unmetered, single-attempt generation + render check against the
// CURRENT pipeline, so an admin can see whether a kit fix actually helps
// before deciding whether to flag the real affected topics below.
export async function replayKitGapExample({ instruction, topicType, targetKitKey }) {
  const res = await replayKitGapExampleFn({ instruction, topicType, targetKitKey });
  return res.data; // {instruction, topicType, targetKitKey, classifiedLibraries, usedTargetKit, stage, verdict, renderOk?, renderErrors?, error?}
}

// Flags the real topics behind a theme's examples with a small, dismissible
// notice the content owner sees in Studio (see functions/kitGapFlagging.js)
// — never a regeneration, never billed to them, purely their own choice
// whether to act on it.
export async function flagKitGapTopics({ examples, themeTitle, suggestedKitName }) {
  const res = await flagKitGapTopicsFn({ examples, themeTitle, suggestedKitName });
  return res.data; // {flagged, results:[{uid,courseId,topicId,ok,reason?}]}
}

// One bounded batch of functions/embeddingBackfill.js's work per call — see
// that file's header. The caller (wireEmbeddingBackfillControls below)
// calls this in a loop until status is 'done'.
export async function runEmbeddingBackfill() {
  const res = await runEmbeddingBackfillFn();
  return res.data;
}

export async function getEmbeddingBackfillStatus() {
  const res = await getEmbeddingBackfillStatusFn();
  return res.data; // { status: 'not_started' } | { status, phase, topics, listings }
}

// One bounded batch of functions/topicOwnerBackfill.js's work per call —
// stamps `ownerUid` on pre-existing topics so searchAllMyTopics
// (functions/search.js) can find them. Same poll-in-a-loop shape as
// runEmbeddingBackfill above.
export async function runTopicOwnerBackfill() {
  const res = await runTopicOwnerBackfillFn();
  return res.data;
}

export async function getTopicOwnerBackfillStatus() {
  const res = await getTopicOwnerBackfillStatusFn();
  return res.data; // { status: 'not_started' } | { status, processed, skipped, errors, userIndex, totalUsers }
}
